import {
  Button,
  Dialog,
  DialogBackdrop,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  cn,
} from "@autonoma/blacklight";
import { ArrowSquareOutIcon } from "@phosphor-icons/react/ArrowSquareOut";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { GitBranchIcon } from "@phosphor-icons/react/GitBranch";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/MagnifyingGlass";
import { useQueryClient } from "@tanstack/react-query";
import { GITHUB_INSTALLED_RETURN_PATH, useGithubConfig, useGithubRepositories } from "lib/query/github.queries";
import { trpc } from "lib/trpc";
import { Suspense, type FormEvent, useState } from "react";
import { nextDraftId, type RepoDraft } from "./topology-draft";

// Cap the install-tab close poll so it can never leak indefinitely (~5 min).
const INSTALL_POLL_INTERVAL_MS = 800;
const INSTALL_POLL_MAX_TICKS = 375;
const MULTIREPO_DOCS_URL = "https://docs.autonoma.app/preview-environments/multirepo/";
// Below this many total repos a search box is more clutter than help.
const SEARCH_THRESHOLD = 6;

interface AddAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Full name (`owner/repo`) of the primary repo, excluded from the picker. */
  primaryRepoFullName?: string;
  /** Dependency repos already in the config (each has at least one app). */
  repos: RepoDraft[];
  /** Adds an app to a repo that is already part of the config (its `owner/repo` full name). */
  onAddToExistingRepo: (repository: string) => void;
  /** Registers a new dependency repo and seeds its first app. */
  onAddToNewRepo: (repo: RepoDraft) => void;
}

/**
 * Asks which repo a new app comes from, in place, when adding an app from a repo
 * other than the primary one. Existing dependency repos can be reused; picking a
 * newly-connected repo captures its fallback branch here so the repo's settings
 * live next to the app instead of in a separate top-of-page band.
 */
export function AddAppDialog({ open, onOpenChange, ...bodyProps }: AddAppDialogProps) {
  // The frame is outside the boundary so the dialog appears the instant it is asked for. The repo list and
  // install config are read inside `AddAppDialogBody`, which suspends - before this split those reads sat
  // above the `<Dialog>`, so clicking "add an app" did nothing visible until GitHub answered.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogBackdrop />
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add an app from another repo</DialogTitle>
          <DialogDescription>
            Pick the repository this app is built from. Its apps deploy into the same preview environment as your
            primary repo.
          </DialogDescription>
        </DialogHeader>
        <Suspense fallback={<AddAppDialogBodySkeleton />}>
          <AddAppDialogBody onOpenChange={onOpenChange} {...bodyProps} />
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function AddAppDialogBodySkeleton() {
  return (
    <>
      <div className="flex flex-col gap-2 px-6 pb-2">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
      </div>
      <DialogFooter>
        <DialogClose render={<Button variant="outline" type="button" />}>Cancel</DialogClose>
        <Button type="button" disabled>
          Add app
        </Button>
      </DialogFooter>
    </>
  );
}

type AddAppDialogBodyProps = Omit<AddAppDialogProps, "open">;

function AddAppDialogBody({
  onOpenChange,
  primaryRepoFullName,
  repos,
  onAddToExistingRepo,
  onAddToNewRepo,
}: AddAppDialogBodyProps) {
  const queryClient = useQueryClient();
  const { data: installationRepos } = useGithubRepositories();
  // Opened in a new tab, so GitHub returns to the "close this tab" page - not back
  // to this dialog's page in a second tab.
  const { data: githubConfig } = useGithubConfig(GITHUB_INSTALLED_RETURN_PATH);
  // `dep:<fullName>` for an existing dependency repo, `new:<fullName>` for a repo to add.
  const [selection, setSelection] = useState<string | undefined>(undefined);
  const [fallbackBranch, setFallbackBranch] = useState("");
  const [query, setQuery] = useState("");

  const usedFullNames = new Set([primaryRepoFullName, ...repos.map((repo) => repo.repo)]);
  const availableRepos = installationRepos.filter((repo) => !usedFullNames.has(repo.fullName));
  const installUrl = githubConfig.installUrl;

  const showSearch = repos.length + availableRepos.length > SEARCH_THRESHOLD;
  const filteredDeps = filterRepos(repos, query, (repo) => repo.repo);
  const filteredAvailable = filterRepos(availableRepos, query, (repo) => repo.fullName);
  const noMatches = query.trim() !== "" && filteredDeps.length === 0 && filteredAvailable.length === 0;

  const selectedNewRepo =
    selection?.startsWith("new:") === true
      ? availableRepos.find((repo) => `new:${repo.fullName}` === selection)
      : undefined;

  function reset() {
    setSelection(undefined);
    setFallbackBranch("");
    setQuery("");
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function selectNewRepo(fullName: string, defaultBranch: string) {
    setSelection(`new:${fullName}`);
    setFallbackBranch(defaultBranch);
  }

  // Open the GitHub App install in a separate tab so the in-progress config draft
  // in this tab is never destroyed by a full-page redirect. The repo list refetches
  // on window focus (see useGithubRepositories); polling the tab's close is a
  // backstop that refreshes even if the user never refocuses before the picker.
  function openGithubInstall() {
    if (installUrl == null) return;
    const installTab = window.open(installUrl, "_blank");
    if (installTab == null) return;
    let ticks = 0;
    const timer = window.setInterval(() => {
      ticks += 1;
      if (!installTab.closed && ticks < INSTALL_POLL_MAX_TICKS) return;
      window.clearInterval(timer);
      void queryClient.invalidateQueries({ queryKey: trpc.github.listRepositories.queryKey() });
    }, INSTALL_POLL_INTERVAL_MS);
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (selection == null) return;
    if (selectedNewRepo != null) {
      const draft: RepoDraft = {
        id: nextDraftId(),
        repo: selectedNewRepo.fullName,
        fallbackBranch: fallbackBranch.trim() === "" ? selectedNewRepo.defaultBranch : fallbackBranch.trim(),
        githubRepositoryId: selectedNewRepo.id,
      };
      onAddToNewRepo(draft);
    } else if (selection.startsWith("dep:")) {
      onAddToExistingRepo(selection.slice("dep:".length));
    }
    handleOpenChange(false);
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-4 px-6 pb-2">
        {repos.length === 0 && availableRepos.length === 0 ? (
          <p className="text-sm text-text-secondary">
            No other repos are connected to the Autonoma GitHub App yet. Grant it access to the repo you need - this tab
            keeps your progress.
          </p>
        ) : (
          <div className="space-y-2">
            {showSearch ? (
              <div className="relative">
                <MagnifyingGlassIcon
                  size={14}
                  className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-3 text-text-secondary"
                />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search repos"
                  className="pl-9"
                  autoFocus
                />
              </div>
            ) : undefined}
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {filteredDeps.map((repo) => (
                <RepoOption
                  key={`dep:${repo.repo}`}
                  selected={selection === `dep:${repo.repo}`}
                  title={repo.repo}
                  subtitle="Already added"
                  onSelect={() => setSelection(`dep:${repo.repo}`)}
                />
              ))}
              {filteredAvailable.map((repo) => (
                <RepoOption
                  key={`new:${repo.fullName}`}
                  selected={selection === `new:${repo.fullName}`}
                  title={repo.fullName}
                  subtitle="Connect to this preview"
                  onSelect={() => selectNewRepo(repo.fullName, repo.defaultBranch)}
                />
              ))}
              {noMatches ? (
                <p className="px-1 py-6 text-center text-2xs text-text-secondary">
                  No repos match &ldquo;{query.trim()}&rdquo;.
                </p>
              ) : undefined}
            </div>
          </div>
        )}

        {selectedNewRepo != null ? (
          <div className="space-y-3 border border-border-dim bg-surface-raised p-4">
            <div className="max-w-xs">
              <Label htmlFor="pk-add-app-fallback">Fallback branch</Label>
              <Input
                id="pk-add-app-fallback"
                value={fallbackBranch}
                onChange={(event) => setFallbackBranch(event.target.value)}
                placeholder="main"
                className="font-mono"
              />
              <p className="mt-1 text-2xs text-text-secondary">
                Deployed when branch matching finds no matching branch.{" "}
                <a
                  href={MULTIREPO_DOCS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary-ink underline underline-offset-2"
                >
                  Learn more
                </a>
              </p>
            </div>
          </div>
        ) : undefined}

        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-fit gap-2 text-text-secondary"
          onClick={openGithubInstall}
          disabled={installUrl == null}
        >
          <GitBranchIcon size={14} />
          Connect another repo on GitHub
          <ArrowSquareOutIcon size={13} />
        </Button>
      </div>

      <DialogFooter>
        <DialogClose render={<Button variant="outline" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={selection == null}>
          Add app
        </Button>
      </DialogFooter>
    </form>
  );
}

function RepoOption({
  selected,
  title,
  subtitle,
  onSelect,
}: {
  selected: boolean;
  title: string;
  subtitle: string;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex w-full items-center gap-3 border px-4 py-3 text-left transition-colors",
        selected ? "border-primary-ink bg-accent-dim" : "border-border-dim hover:border-primary-ink/50",
      )}
    >
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-sm text-text-primary" title={title}>
          {title}
        </p>
        <p className="text-2xs text-text-secondary">{subtitle}</p>
      </div>
      {selected ? <CheckCircleIcon size={18} weight="fill" className="shrink-0 text-primary-ink" /> : undefined}
    </button>
  );
}

/** Base offset so any subsequence-only match sorts after every contiguous-substring match. */
const SUBSEQUENCE_PENALTY = 1000;

/**
 * Frontend-only fuzzy match: case-insensitive, returns a rank (lower is better) or
 * `undefined` when the query's characters don't appear in order. A contiguous
 * substring ranks by its position; a gapped subsequence ranks strictly worse.
 */
function fuzzyScore(query: string, text: string): number | undefined {
  const q = query.trim().toLowerCase();
  if (q === "") return 0;
  const t = text.toLowerCase();
  const direct = t.indexOf(q);
  if (direct !== -1) return direct;

  let cursor = 0;
  let gaps = 0;
  let firstIndex = -1;
  for (const char of q) {
    const found = t.indexOf(char, cursor);
    if (found === -1) return undefined;
    if (firstIndex === -1) firstIndex = found;
    if (found > cursor) gaps += found - cursor;
    cursor = found + 1;
  }
  return SUBSEQUENCE_PENALTY + firstIndex + gaps;
}

/** Keeps only items whose text fuzzy-matches the query, best matches first. */
function filterRepos<T>(items: T[], query: string, textOf: (item: T) => string): T[] {
  return items
    .map((item) => ({ item, score: fuzzyScore(query, textOf(item)) }))
    .filter((entry) => entry.score != null)
    .sort((a, b) => (a.score ?? 0) - (b.score ?? 0))
    .map((entry) => entry.item);
}
